<template>
  <card-dialog
    v-model="modalVisible"
    max-width="400px"
    :title="periodTagFormData.id ? $t('card_title.edit_period_tag') : $t('card_title.add_period_tag') "
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="periodTagFormData.name"
            :error-messages="errors"
            :label="$t('text_input.period_tag_name.title')"
            :placeholder="$t('text_input.period_tag_name.placeholder')"
            outlined
            dense
          />
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="periodTagFormData.id"
            color="error"
            text
            block
            class="mt-1"
            @click="toggleConfirmationDialog(true)"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deletePeriodTag()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { PeriodTagApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { cloneDeep } from 'lodash'

const emptyPeriodTagFormData = {
  id: undefined,
  name: ''
}

export default {
  name: 'PeriodTagFormDialog',
  components: { DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      periodTagFormData: cloneDeep(emptyPeriodTagFormData),
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_PERIOD_TAG_FORM, () => {
      this.modalVisible = true
      this.periodTagFormData = cloneDeep(emptyPeriodTagFormData)

      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    })

    EventBus.$on(this.$events.EDIT_PERIOD_TAG, (periodTag) => {
      this.modalVisible = true

      this.periodTagFormData = {
        id: periodTag.id,
        name: periodTag.name
      }
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let result

        if (this.periodTagFormData.id) {
          result = await PeriodTagApiService.update({
            id: this.periodTagFormData.id,
            name: this.periodTagFormData.name
          })
        } else {
          result = await PeriodTagApiService.create({
            name: this.periodTagFormData.name
          })
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.period_tag_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.periodTagFormData.id) {
          EventBus.$emit(this.$events.PERIOD_TAG_UPDATED)
        } else {
          EventBus.$emit(this.$events.PERIOD_TAG_CREATED)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.period_tag_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deletePeriodTag() {
      try {
        this.deleting = true

        await PeriodTagApiService.delete(this.periodTagFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.period_tag_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.PERIOD_TAG_DELETED, this.periodTagFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.period_tag_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
